import React, { useState, useRef, useEffect, useCallback } from "react";
import { theme, baseStyle } from "../../theme";
import { AREAS, CATEGORIES, JOB_TYPES as JOB_TYPE_OPTIONS } from "../../constants";
import StatusBadge from "../ui/StatusBadge";
import SkeletonCard from "../ui/SkeletonCard";
import TrustBadge from "../ui/TrustBadge";
import Header from "../layout/Header";
import BottomNav from "../layout/BottomNav";
import Notification from "../layout/Notification";
import { imagesApi } from "../../utils/api";
import Avatar from "../ui/Avatar";
import { playBell } from "../../utils/sounds";
import { getNearestArea } from "../../utils/location";

const LOCALSTORAGE_GPS_AREA_KEY = "greidi_gps_area";

export default function FeedScreen({
  jobs,
  jobsLoading,
  jobsError,
  filterCat,
  setFilterCat,
  selectedArea,
  setSelectedArea,
  goJob,
  notify,
  notification,
  onRefresh,
  // BottomNav props
  activeTab,
  unreadCount,
  goFeed,
  goSearch,
  goNewJob,
  goCalendar,
  goMyPage,
}) {
  // Local UI state
  const [filterJobType, setFilterJobType] = useState(null);
  const [devBannerDismissed, setDevBannerDismissed] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [gpsBanner, setGpsBanner] = useState(null); // { area, distance }

  // On mount, restore GPS area preference from localStorage
  useEffect(() => {
    const savedGpsArea = localStorage.getItem(LOCALSTORAGE_GPS_AREA_KEY);
    if (savedGpsArea && setSelectedArea && (!selectedArea || selectedArea === "Allt")) {
      setSelectedArea(savedGpsArea);
      setGpsBanner({ area: savedGpsArea, distance: null });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = jobs.filter(j => {
    if (selectedArea && selectedArea !== "Allt" && j.area !== selectedArea) return false;
    if (filterCat && j.category !== filterCat) return false;
    if (filterJobType && (j.jobType || j.job_type || "standard") !== filterJobType) return false;
    return true;
  });

  // Pull-to-refresh state
  const [pulling, setPulling] = useState(false);
  const [pullY, setPullY] = useState(0);
  const startYRef = useRef(0);

  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      startYRef.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (!startYRef.current) return;
    const diff = e.touches[0].clientY - startYRef.current;
    if (diff > 0 && diff < 120 && window.scrollY === 0) {
      setPullY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (pullY > 60 && onRefresh) {
      setPulling(true);
      Promise.resolve(onRefresh()).finally(() => {
        setPulling(false);
        setPullY(0);
      });
    } else {
      setPullY(0);
    }
    startYRef.current = 0;
  };

  // GPS nearby handler
  const handleNearbyClick = useCallback(() => {
    if (gpsLoading) return;

    const hasGeolocation = typeof navigator !== "undefined" && "geolocation" in navigator;
    if (!hasGeolocation) {
      setGpsError("Staðsetningartækni ekki tiltæk");
      setTimeout(() => setGpsError(null), 4000);
      return;
    }

    setGpsLoading(true);
    setGpsError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const result = getNearestArea(latitude, longitude);

        if (result.area) {
          setSelectedArea(result.area);
          localStorage.setItem(LOCALSTORAGE_GPS_AREA_KEY, result.area);
          setGpsBanner({ area: result.area, distance: result.distance });
        }
        setGpsLoading(false);
      },
      (error) => {
        setGpsLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          setGpsError("Staðsetning hafnað — veldu svæði handvirkt");
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setGpsError("Gat ekki fundið staðsetningu");
        } else if (error.code === error.TIMEOUT) {
          setGpsError("Staðsetning tók of langan tíma");
        } else {
          setGpsError("Gat ekki fundið staðsetningu");
        }
        setTimeout(() => setGpsError(null), 4000);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, [gpsLoading, setSelectedArea]);

  // Clear area filter (and GPS banner)
  const handleClearArea = () => {
    setSelectedArea("Allt");
    setGpsBanner(null);
    localStorage.removeItem(LOCALSTORAGE_GPS_AREA_KEY);
  };

  return (
    <>
    <style>{`.job-img-wrap:hover .job-photo-overlay { opacity: 1 !important; }
@keyframes gps-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    <div
      style={baseStyle}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Header goFeed={goFeed} right={
        <button onClick={() => { playBell(); notify("🔔 Engar nýjar tilkynningar"); }} aria-label="Tilkynningar" style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer" }}>🔔</button>
      } />

      {/* Development disclaimer — dismissible floating tooltip */}
      {!devBannerDismissed && (
        <div style={{ margin: "0 16px", padding: "8px 12px", background: "linear-gradient(135deg, #f59e0b22, #f59e0b11)", border: "1px solid #f59e0b44", borderRadius: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>🚧</span>
          <div style={{ flex: 1, fontSize: 11, color: "#f59e0b" }}>Appið er í þróun — verk hér eru ekki raunveruleg enn.</div>
          <button onClick={() => setDevBannerDismissed(true)} style={{ background: "none", border: "none", color: "#f59e0b88", fontSize: 16, cursor: "pointer", padding: "4px 6px", lineHeight: 1 }} aria-label="Loka">✕</button>
        </div>
      )}

      {/* Pull-to-refresh indicator */}
      {(pullY > 0 || pulling) && (
        <div style={{
          textAlign: "center", padding: "8px 0",
          transition: "all 0.2s", opacity: pullY > 30 || pulling ? 1 : pullY / 30,
          transform: `translateY(${Math.min(pullY * 0.4, 40)}px)`,
        }}>
          <span style={{ fontSize: 20, display: "inline-block", animation: pulling ? "spin 1s linear infinite" : "none" }}>
            {pulling ? "🔄" : pullY > 60 ? "⬇️" : "⬆️"}
          </span>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            {pulling ? "Endurhleð..." : pullY > 60 ? "Slepptu til að endurhlaða" : "Dragðu niður..."}
          </div>
        </div>
      )}

      {/* Area filter — GPS button + area pills */}
      <div style={{ padding: "8px 16px 4px", display: "flex", gap: 8, overflowX: "auto", alignItems: "center" }}>
        {/* GPS "Near me" button */}
        <button
          onClick={handleNearbyClick}
          disabled={gpsLoading}
          aria-label="Nálægt mér"
          style={{
            minWidth: 44, height: 44, borderRadius: 22,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            background: gpsBanner ? `${theme.green}22` : theme.card,
            border: gpsBanner ? `2px solid ${theme.green}` : `1px solid ${theme.border}`,
            color: gpsBanner ? theme.green : theme.textMuted,
            fontSize: 13, fontWeight: 600, cursor: gpsLoading ? "wait" : "pointer",
            whiteSpace: "nowrap", flexShrink: 0, padding: "0 14px",
            transition: "all 0.2s", opacity: gpsLoading ? 0.7 : 1,
          }}
        >
          <span style={{
            fontSize: 16,
            display: "inline-block",
            animation: gpsLoading ? "gps-pulse 1s ease-in-out infinite" : "none",
          }}>
            {gpsLoading ? "🔍" : "📍"}
          </span>
          Nálægt mér
        </button>

        {/* "All" pill */}
        <button
          onClick={handleClearArea}
          style={{
            height: 36, borderRadius: 18,
            background: (!selectedArea || selectedArea === "Allt") ? theme.accent : theme.card,
            color: (!selectedArea || selectedArea === "Allt") ? "#0a0f1a" : theme.textMuted,
            border: "none", padding: "0 14px", fontSize: 13, fontWeight: 600,
            cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            transition: "all 0.2s",
          }}
        >Allt</button>

        {/* Area pills */}
        {AREAS.map(a => (
          <button
            key={a}
            onClick={() => {
              if (selectedArea === a) {
                handleClearArea();
              } else {
                setSelectedArea(a);
                setGpsBanner(null);
                localStorage.removeItem(LOCALSTORAGE_GPS_AREA_KEY);
              }
            }}
            style={{
              height: 36, borderRadius: 18,
              background: selectedArea === a ? theme.accent : theme.card,
              color: selectedArea === a ? "#0a0f1a" : theme.textMuted,
              border: "none", padding: "0 14px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
              transition: "all 0.2s",
            }}
          >{a}</button>
        ))}
      </div>

      {/* GPS error message */}
      {gpsError && (
        <div style={{
          margin: "4px 16px 0", padding: "8px 12px",
          background: "rgba(248,113,113,0.1)", border: `1px solid ${theme.red}44`,
          borderRadius: 8, fontSize: 12, color: theme.red,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span>⚠️</span> {gpsError}
        </div>
      )}

      {/* GPS location banner */}
      {gpsBanner && !gpsError && selectedArea && selectedArea !== "Allt" && (
        <div style={{
          margin: "4px 16px 0", padding: "8px 12px",
          background: `${theme.green}11`, border: `1px solid ${theme.green}33`,
          borderRadius: 8, fontSize: 12, color: theme.green,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span>📍 Sýni verk nálægt þér — {gpsBanner.area}{gpsBanner.distance != null ? ` (~${gpsBanner.distance} km)` : ""}</span>
          <button
            onClick={handleClearArea}
            aria-label="Hreinsa svæðissíu"
            style={{
              background: "none", border: "none", color: theme.green,
              fontSize: 14, cursor: "pointer", padding: "2px 4px", lineHeight: 1,
            }}
          >✕</button>
        </div>
      )}

      {/* Category filter - icon circles, tap again to deselect */}
      <div style={{ padding: "12px 20px 10px", display: "flex", gap: 6, justifyContent: "center" }}>
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setFilterCat(filterCat === c.id ? null : c.id)} style={{
            width: 52, height: 52, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            background: filterCat === c.id ? `${c.color}22` : "transparent",
            border: filterCat === c.id ? `2px solid ${c.color}` : `2px solid transparent`,
            fontSize: 20, cursor: "pointer", transition: "all 0.2s", overflow: "hidden",
            boxShadow: filterCat === c.id ? `0 0 12px ${c.color}44` : "none",
          }} title={c.label}>
            {c.iconUrl ? <img src={c.iconUrl} alt={c.label} style={{ width: 44, height: 44, objectFit: "contain" }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = ''; }} /> : null}
            <span style={c.iconUrl ? { display: "none" } : {}}>{c.icon}</span>
          </button>
        ))}
      </div>
      {filterCat && <div style={{ textAlign: "center", fontSize: 11, color: CATEGORIES.find(c=>c.id===filterCat)?.color, fontWeight: 600, marginTop: -6, marginBottom: 4 }}>{CATEGORIES.find(c=>c.id===filterCat)?.label}</div>}

      {/* Job type filter - icon circles, tap again to deselect */}
      <div style={{ padding: "2px 20px 10px", display: "flex", gap: 6, justifyContent: "center" }}>
        {JOB_TYPE_OPTIONS.map(opt => {
          const isActive = filterJobType === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setFilterJobType(isActive ? null : opt.id)}
              style={{
                width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: isActive ? "rgba(56,189,248,0.15)" : "transparent",
                border: isActive ? `2px solid ${theme.accent}` : `2px solid transparent`,
                fontSize: 16, cursor: "pointer", transition: "all 0.2s", overflow: "hidden",
              }}
              title={opt.label}
            >
              {opt.iconUrl ? <img src={opt.iconUrl} alt={opt.label} style={{ width: 36, height: 36, objectFit: "contain" }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = ''; }} /> : null}
              <span style={opt.iconUrl ? { display: "none" } : {}}>{opt.icon}</span>
            </button>
          );
        })}
      </div>
      {filterJobType && <div style={{ textAlign: "center", fontSize: 11, color: theme.accent, fontWeight: 600, marginTop: -6, marginBottom: 4 }}>{JOB_TYPE_OPTIONS.find(o => o.id === filterJobType)?.label}</div>}

      {/* Loading state */}
      {jobsLoading && (
        <div style={{ padding: "0 16px 120px", display: "flex", flexDirection: "column", gap: 12 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Error state */}
      {jobsError && !jobsLoading && (
        <div style={{ padding: "40px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div style={{ color: theme.textMuted, fontSize: 14, marginBottom: 12 }}>{jobsError}</div>
          <button onClick={() => window.location.reload()} aria-label="Reyna aftur" style={{
            padding: "10px 20px", borderRadius: 10, border: `1px solid ${theme.border}`,
            background: theme.card, color: theme.text, fontSize: 14, cursor: "pointer"
          }}>Reyna aftur</button>
        </div>
      )}

      {/* Job cards */}
      {!jobsLoading && !jobsError && (
        <div style={{ padding: "0 16px 120px", display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 16px", color: theme.textMuted }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <div>Engin verk fundust{filterCat || filterJobType || (selectedArea && selectedArea !== "Allt") ? ' með þessum síum' : ''}</div>
            </div>
          )}
          {filtered.map(job => {
            const cat = CATEGORIES.find(c => c.id === job.category);
            const user = job.userName ? {
              name: job.userName,
              avatar: job.userAvatar || '👤',
              rating: job.userRating || 5.0,
              completedJobs: job.user_completed_jobs || 0
            } : { name: 'Notandi', avatar: '👤', rating: 5.0, completedJobs: 0 };
            return (
              <div key={job.id} onClick={() => goJob(job)} role="button" tabIndex={0} aria-label={`Verk: ${job.title || job.description?.slice(0, 40) || 'Ónefnt verk'}`} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') goJob(job); }} style={{
                background: theme.card, borderRadius: 16, padding: 0, cursor: "pointer",
                border: `1px solid ${theme.border}`, overflow: "hidden",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.3)`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
              >
                {/* Job image area — SVG illustration default, hover reveals original photo */}
                <div className="job-img-wrap" style={{ height: 160, background: `linear-gradient(135deg, ${cat.color}33, ${theme.inputBg})`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
                  {job.iconUrl ? (
                    <img src={imagesApi.getUrl(job.iconUrl)} alt={job.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : job.img && job.img !== "🖼️" ? (
                    <img src={imagesApi.getUrl(job.img)} alt={job.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <span style={{ fontSize: 56, opacity: 0.6 }}>{cat.icon}</span>
                  )}
                  {job.iconUrl && job.img && job.img !== "🖼️" && (
                    <img
                      src={imagesApi.getUrl(job.img)}
                      alt="Upprunaleg mynd"
                      className="job-photo-overlay"
                      style={{
                        position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "cover", opacity: 0, transition: "opacity 0.35s ease",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                  <div style={{ position: "absolute", top: 12, left: 12 }}>
                    <StatusBadge status={job.status} />
                  </div>
                  <div style={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 6, alignItems: "center" }}>
                    {job.jobType && job.jobType !== "standard" && (() => {
                      const jt = JOB_TYPE_OPTIONS.find(t => t.id === job.jobType);
                      if (!jt) return null;
                      const color = job.jobType === "gefa" ? "#34d399" : job.jobType === "senda_og_henda" ? "#38bdf8" : "#f59e0b";
                      return (
                        <span style={{ background: `${color}cc`, borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#0a0f1a" }}>
                          {jt.icon} {jt.label}
                        </span>
                      );
                    })()}
                    <span style={{ background: "rgba(0,0,0,0.6)", borderRadius: 8, padding: "4px 10px", fontSize: 11, color: theme.textMuted }}>
                      {job.timeAgo}
                    </span>
                  </div>
                </div>

                <div style={{ padding: "14px 16px 16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 6 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{job.title}</h3>
                    <span style={{ color: theme.accent, fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", marginLeft: 12 }}>{job.price}</span>
                  </div>

                  <p style={{ margin: "0 0 10px", fontSize: 13, color: theme.textMuted, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {job.desc}
                  </p>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <Avatar src={user.avatar} size={18} />
                      <span style={{ fontSize: 13, color: theme.textMuted }}>{user.name}</span>
                      <span style={{ fontSize: 11, color: theme.orange }}>⭐ {user.rating}</span>
                      <TrustBadge completedJobs={user.completedJobs || 0} rating={user.rating || 0} size="sm" />
                    </div>
                    <div style={{ fontSize: 12, color: theme.textDim }}>
                      📍 {job.area || "Allt"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <BottomNav activeTab={activeTab} unreadCount={unreadCount} goFeed={goFeed} goSearch={goSearch} goNewJob={goNewJob} goCalendar={goCalendar} goMyPage={goMyPage} />
      <Notification message={notification} />
    </div>
    </>
  );
}
